/**
 * Theme front script (ESM) — cookie, forms, motion dynamic import.
 */

const cfg = window.potokTheme || {};
const cookieName = cfg.cookieName || 'potok_cookie_consent';
const cookiePath = cfg.cookiePath || '/';
const cookieMaxAge = cfg.cookieMaxAge || 31536000;

function getCookie(name) {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : '';
}

function setCookie(name, value, maxAge, path) {
  const secure = location.protocol === 'https:' ? '; Secure' : '';
  document.cookie =
    name + '=' + encodeURIComponent(value) + '; Max-Age=' + maxAge + '; Path=' + path + '; SameSite=Lax' + secure;
}

function initDataLayer() {
  window.dataLayer = window.dataLayer || [];
}

function pushEvent(name, params) {
  initDataLayer();
  const payload = Object.assign({ event: name }, params || {});
  window.dataLayer.push(payload);
  if (cfg.debugAnalytics && typeof console !== 'undefined' && console.debug) {
    console.debug('[potok analytics]', payload);
  }
}

function loadAnalyticsAfterConsent() {
  pushEvent('potok_analytics_ready', { consent: true });
}

function initCookieBanner() {
  const banner = document.getElementById('potok-cookie-banner');
  const accept = document.getElementById('potok-cookie-accept');
  if (!banner || !accept) {
    return;
  }

  if (getCookie(cookieName) === '1') {
    loadAnalyticsAfterConsent();
    return;
  }

  banner.classList.add('is-visible');
  banner.removeAttribute('hidden');

  accept.addEventListener('click', () => {
    setCookie(cookieName, '1', cookieMaxAge, cookiePath);
    try {
      localStorage.setItem('potok_cookie_consent', 'accepted');
    } catch (e) {
      /* ignore */
    }
    banner.classList.remove('is-visible');
    pushEvent('potok_cookie_accept', {});
    loadAnalyticsAfterConsent();
  });
}

function initCtaTracking() {
  document.addEventListener('click', (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) {
      return;
    }
    const el = target.closest('[data-cta-id]');
    if (!el) {
      return;
    }
    pushEvent('potok_cta_click', {
      cta_id: el.getAttribute('data-cta-id') || '',
      page_path: location.pathname,
    });
  });

  document.addEventListener('click', (ev) => {
    const target = ev.target;
    if (!(target instanceof Element)) {
      return;
    }
    let link = target.closest('a[href^="tel:"]');
    if (link) {
      pushEvent('potok_phone_click', { location: link.getAttribute('data-cta-id') || 'inline' });
      return;
    }
    link = target.closest('a[href^="mailto:"]');
    if (link) {
      pushEvent('potok_email_click', { location: link.getAttribute('data-cta-id') || 'inline' });
    }
  });
}

function initReducedMotion() {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (reduced) {
    document.documentElement.setAttribute('data-reduced-motion', 'true');
  }
  return reduced;
}

function initHeaderScroll() {
  const header = document.querySelector('[data-potok-header]');
  if (!header) {
    return;
  }
  const hero = document.getElementById('hero');
  const onScroll = () => {
    let scrolled = window.scrollY > 8;
    if (hero) {
      const rect = hero.getBoundingClientRect();
      scrolled = rect.bottom < 72;
    }
    header.classList.toggle('is-scrolled', scrolled);
  };
  onScroll();
  window.addEventListener('scroll', onScroll, { passive: true });
}

function initFaqAccordion() {
  document.querySelectorAll('.faq-item__trigger').forEach((btn) => {
    btn.addEventListener('click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      const panelId = btn.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      if (panel) {
        if (expanded) {
          panel.setAttribute('hidden', '');
        } else {
          panel.removeAttribute('hidden');
        }
      }
      const icon = btn.querySelector('.faq-item__icon');
      if (icon) {
        icon.textContent = expanded ? '+' : '−';
      }
    });
  });
}

function initLeadForm() {
  const form = document.getElementById('potok-lead-form');
  if (!form) {
    return;
  }
  const status = document.getElementById('potok-lead-status');
  form.addEventListener('submit', (ev) => {
    ev.preventDefault();
    if (!form.checkValidity()) {
      form.reportValidity();
      return;
    }
    const fd = new FormData(form);
    const payload = {
      name: fd.get('name'),
      phone: fd.get('phone'),
      email: fd.get('email'),
      interest: fd.get('interest'),
      message: fd.get('message'),
      website_url: fd.get('website_url'),
      pd_consent: fd.get('pd_consent') ? 1 : 0,
      _wpnonce: cfg.nonce || '',
    };
    fetch((cfg.restUrl || '') + 'lead', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-WP-Nonce': cfg.nonce || '' },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then((result) => {
        if (status) {
          status.textContent =
            (result.data && result.data.message) || (result.ok ? 'Заявка отправлена.' : 'Ошибка отправки.');
          status.classList.toggle('is-error', !result.ok);
        }
        if (result.ok && result.data && result.data.ok) {
          form.reset();
          pushEvent('potok_lead_submit', { status: 'success' });
        }
      })
      .catch(() => {
        if (status) {
          status.textContent =
            'Не удалось отправить форму. Проверьте поля и попробуйте снова или напишите на hello@potok-zadach-demo.ru.';
          status.classList.add('is-error');
        }
      });
  });
}

async function initMotionHooks() {
  initReducedMotion();
  const isHome = document.body.classList.contains('home') || document.querySelector('.potok-main--front');
  const lite = document.querySelector('[data-potok-motion="lite"]');
  const reduced = document.documentElement.getAttribute('data-reduced-motion') === 'true';

  const run = async () => {
    if (reduced) {
      document.documentElement.classList.add('js-motion-ready');
      return;
    }
    try {
      if (isHome && cfg.motionHomeUrl) {
        const mod = await import(/* webpackIgnore: true */ cfg.motionHomeUrl);
        const init = mod.default || mod.initMotionHome;
        if (typeof init === 'function') {
          init();
        }
      } else if (lite && cfg.motionLiteUrl) {
        const mod = await import(/* webpackIgnore: true */ cfg.motionLiteUrl);
        const init = mod.default || mod.initMotionLite;
        if (typeof init === 'function') {
          init();
        }
      }
    } catch (err) {
      if (cfg.debugAnalytics && console.warn) {
        console.warn('[potok motion load]', err);
      }
    }
    document.documentElement.classList.add('js-motion-ready');
  };

  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => run(), { timeout: 1200 });
  } else {
    setTimeout(run, 80);
  }
}

function boot() {
  initCookieBanner();
  initCtaTracking();
  initHeaderScroll();
  initFaqAccordion();
  initLeadForm();
  initMotionHooks();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}
